import { Components, DateUtils, ITaskRunner, List, ModelBase } from '@singularsystems/neo-core';
import * as Awards from 'st-awards';
import AppDataLookup from '../Models/Portfolio/AppDataLookup';
import InstrumentLookup from 'st-common/Transactions/Models/InstrumentLookup';
import { AppService, Types } from '../TransactionsTypes';
import { ILoadOptions, IPortfolioService } from './IPortfolioService';
import { injectable } from 'inversify';
import AmountWithCurrency from '../Models/AmountWithCurrency';
import FutureAmounts, { IFutureAmountInput } from '../Models/FutureAmounts';
import TradeRequestLookup from '../Models/Trading/Lookups/TradeRequestLookup';
import { PortfolioExchangeRateService } from "./PortfolioExchangeRateService";
import CurrencyLookup from 'st-common/Transactions/Models/CurrencyLookup';

@injectable()
export default class PortfolioService extends ModelBase implements IPortfolioService {

    constructor(
        private apiClient = AppService.get(Types.Transactions.ApiClients.PortfolioApiClient),
        private awardsDataCache = AppService.get(Types.Awards.Services.AwardsDataCache),
        private authService = AppService.get(Types.Shared.Services.STAuthenticationService)) {
        super();
 
        this.makeObservable();
    }

    private lastLoadDate: Date | null = null;
    private lastParticipantId: string = "";
    
    public appData: AppDataLookup | null = null;

    public exchangeRateService: PortfolioExchangeRateService | null = null;

    public get hasData() {
        return this.appData != null;
    }

    public pendingTradesTask = AppService.get(Types.Neo.TaskRunner);

    public async ensureLoaded(taskRunner: ITaskRunner, loadOptions?: ILoadOptions, onError = Components.ErrorDisplayType.ShowModal) {
        
        loadOptions = loadOptions ?? {};
        let appData = this.appData;

        if (!appData || this.lastLoadDate === null || DateUtils.difference(this.lastLoadDate, new Date()).totalMinutes > 60 || this.authService.user?.participantId !== this.lastParticipantId) {
            const fetchCurrencyId = PortfolioExchangeRateService.getCurrencyIdFromStorage();
      
            const results = await taskRunner.waitForAll({
                appData: this.apiClient.getParticipantsAppData(fetchCurrencyId, loadOptions.fetchPendingTrades === true),
                instruments: this.awardsDataCache.instruments.getDataAsync(),
                incentiveSchemes: this.awardsDataCache.incentiveSchemes.getDataAsync()
            }, { errorHandling: onError });      
            const instrumentList = List.fromJSArray(InstrumentLookup, results.instruments);
            const incentiveSchemes = List.fromJSArray(Awards.IncentiveSchemeLookup, results.incentiveSchemes);
            
            appData = AppDataLookup.fromJSObject<AppDataLookup>(results.appData.data);
            appData.setup(instrumentList, incentiveSchemes);

            if (loadOptions.fetchPendingTrades === true) {
                appData.setupPendingTradeRequests();
            }
            
            this.lastLoadDate = new Date();
            this.lastParticipantId = this.authService.user?.participantId ?? "";

            this.exchangeRateService = new PortfolioExchangeRateService(appData, fetchCurrencyId ?? appData.settings.baseCurrencyId);
        }
        
        if (loadOptions.fetchPendingTrades && !appData.pendingTradeRequestsLoaded) {
            const result = await this.pendingTradesTask.waitFor(this.apiClient.getPendingTradeRequests());
            appData.pendingTradeRequests = List.fromJSArray(TradeRequestLookup, result.data);
            appData.setupPendingTradeRequests();
        }

        this.exchangeRateService?.setInstrumentRates();

        appData.instrumentEditMode = false;

        this.appData = appData;

        return this.appData!;
    }

    public get vestedProfits() {
        return this.getProfitAmounts(false);
    }

    public get totalProfits() {
        return this.getProfitAmounts(true);
    }

    public calculateFutureAmounts<T extends IFutureAmountInput>(
        currency: CurrencyLookup, 
        balances: T[], 
        valueAccessor: (balance: T) => number,
        valueAccessor2: (balance: T) => number): FutureAmounts {

        const now = new Date();
        const endOfThisYear = new Date(now.getFullYear(), 11, 31);
        let startYear = 0;
        if (!balances.some(c => !c.isVested && c.canTradeDate <= endOfThisYear)) {
            // Nothing else vesting this year, look forward to next year.
            startYear = 1;
        }

        const year1 = new Date(now.getFullYear() + startYear, 11, 31),
            year2 = new Date(now.getFullYear() + startYear + 1, 11, 31),
            year3 = new Date(now.getFullYear() + startYear + 2, 11, 31);

        const futureAmounts = new FutureAmounts(currency, year1.getFullYear().toString(), year2.getFullYear().toString(), year3.getFullYear().toString() + "+");

        futureAmounts.year1Boundary = year1;
        futureAmounts.year2Boundary = year2;

        for (let tranche of balances) {
            const grossValueAtAwardDate = (tranche.marketPriceAtAwardDate ?? 0) * tranche.awardedUnits;

            if (tranche.isVested) {
                futureAmounts.vestedValue += valueAccessor(tranche);
                futureAmounts.vestedValue2 += valueAccessor2(tranche);
                futureAmounts.grossValueAtAwardDateVestedValue += grossValueAtAwardDate;
            } else if (tranche.canTradeDate <= year1) {
                futureAmounts.yearPlus1Value += valueAccessor(tranche);
                futureAmounts.yearPlus1Value2 += valueAccessor2(tranche);
                futureAmounts.grossValueAtAwardDateYearPlus1Value += grossValueAtAwardDate;
            } else if (tranche.canTradeDate <= year2) {
                futureAmounts.yearPlus2Value += valueAccessor(tranche);
                futureAmounts.yearPlus2Value2 += valueAccessor2(tranche);
                futureAmounts.grossValueAtAwardDateYearPlus2Value += grossValueAtAwardDate;
            } else {
                futureAmounts.yearPlus3Value += valueAccessor(tranche);
                futureAmounts.yearPlus3Value2 += valueAccessor2(tranche);
                futureAmounts.grossValueAtAwardDateYearPlus3Value += grossValueAtAwardDate;
            }
            futureAmounts.allTimeValue += valueAccessor(tranche);
            futureAmounts.allTimeValue2 += valueAccessor2(tranche);
            futureAmounts.grossValueAtAwardDateAllTimeValue += grossValueAtAwardDate;
        }

        futureAmounts.vestedGrowthPercentage = this.getGrowthPercentage(futureAmounts.vestedValue, futureAmounts.grossValueAtAwardDateVestedValue);
        futureAmounts.yearPlus1GrowthPercentage = this.getGrowthPercentage(futureAmounts.yearPlus1Value, futureAmounts.grossValueAtAwardDateYearPlus1Value);
        futureAmounts.yearPlus2GrowthPercentage = this.getGrowthPercentage(futureAmounts.yearPlus2Value, futureAmounts.grossValueAtAwardDateYearPlus2Value);
        futureAmounts.yearPlus3GrowthPercentage = this.getGrowthPercentage(futureAmounts.yearPlus3Value, futureAmounts.grossValueAtAwardDateYearPlus3Value);
        futureAmounts.allTimeGrowthPercentage = this.getGrowthPercentage(futureAmounts.allTimeValue, futureAmounts.grossValueAtAwardDateAllTimeValue);

        return futureAmounts;
    }

    public get futureAmounts() {        
        if (this.appData && this.exchangeRateService && !this.exchangeRateService.hasMissingExchangeRates) {
            // Excluding values of hidden schemes
            const visibleSchemes = this.appData!.trancheBalances.filter(c => !c.incentiveScheme.hideFromParticipants);
            return this.calculateFutureAmounts(this.exchangeRateService.lastFetchedCurrency, visibleSchemes, 
                balance => balance.limitLossForTotal(c => c.profitLoss * (c.instrument.lastRate ?? 0)),
                balance => 0);
        }
        return null;
    }

    public expire() {
        this.lastLoadDate = null;
    }

    private getGrowthPercentage(currentValue: number, grossValue: number): number {
        if (grossValue === 0) {
            return 0;
        }
        return ((currentValue / grossValue) - 1) * 100;
    }

    private getProfitAmounts(all: boolean) {
        if (this.exchangeRateService && this.appData) {
            const currency = this.exchangeRateService.lastFetchedCurrency;
            const symbol = currency.symbol;

            return this.appData?.trancheBalances.filter(c => (all ||c.isVested) && !c.incentiveScheme.hideFromParticipants)
                .groupBy(c => c.instrument.lastRate === null ? c.currencySymbol : symbol, (key, i, details) => 
                    new AmountWithCurrency(details.sum(d => d.limitLossForTotal(c => c.profitLoss * (c.instrument.lastRate ?? 0))), key)) ?? [];
        } else {
            return [];
        }
    }
}